<template>
  <div>
    <div
      ref="mapWrapper"
      class="map-wrapper"
      @mousedown="startPan"
      @mousemove="panning"
      @mouseup="endPan"
      @mouseleave="endPan"
      @touchstart.passive="startPan"
      @touchmove="panning"
      @touchend="endPan"
      @touchcancel="endPan"
      @wheel="handleWheel"
    >
      <InlineSvg
        ref="map"
        :src="require('../../../assets/images/new-map.svg')"
        @loaded="onSvgLoaded"
        @click="onMapClick"
      />

      <div class="map-header -yellow gap-1 lg:gap-4" :style="headerStyle" v-if="zoomedLocation">
        <div class="title hidden md:block">
          {{ zoomedLocation.title }}
        </div>
        <span class="hidden md:block">
          {{ humanizeType(zoomedLocation.location_type) }}
        </span>
      </div>

      <Pin
        v-for="loc in locations"
        :key="loc.id"
        :location="loc"
        :scale="scale"
        :offset="offset"
        :min-x="vbMinX"
        :min-y="vbMinY"
        :active-location-id="zoomedLocation ? zoomedLocation.id : null"
      />
      <Labels />
    </div>
  </div>
</template>

<script>
import InlineSvg from 'vue-inline-svg';
import Pin from './components/Pin.vue';
import Labels from './components/Labels.vue';

export default {
  name: 'MapInput',
  components: { InlineSvg, Pin, Labels },
  props: {
    locations: { type: Array, required: true },
    zoomedLocation: { type: Object, required: false },
    disablePannZoom: { type: Boolean, default: false },
    staticLabelValue: { type: String, required: false },
    initialLocation: { type: Object, required: false },
  },

  data() {
    return {
      scale: 1,
      offset: { x: 0, y: 0 },
      coords: { x: 0, y: 0 },
      vbMinX: 0,
      vbMinY: 0,
      vbWidth: 1240,
      vbHeight: 698,
      originalViewBox: '0 0 1240 698',
      isPanning: false,
      startPoint: { x: 0, y: 0 },
      lastPinchDist: null,
      rafId: null,
      pendingViewBoxUpdate: false,
    };
  },

  computed: {
    headerStyle() {
      if (!this.zoomedLocation) return {};
      if (this.zoomedLocation) {
        return {
          left: '50%',
          top: '50%',
          transform: 'translate(-50%, -50%)'
        };
      }

      return {
        left: `${(this.zoomedLocation.coords_x - this.vbMinX) / this.scale + this.offset.x + 20}px`,
        top: `${(this.zoomedLocation.coords_y - this.vbMinY) / this.scale + this.offset.y - 30}px`,
      };
    },
    svg() {
      return this.$refs.map?.$el;
    },
    wrapper() {
      return this.$refs.mapWrapper;
    },
  },

  mounted() {
    window.addEventListener('resize', this.resolveScaleAndOffset);
  },

  beforeDestroy() {
    window.removeEventListener('resize', this.resolveScaleAndOffset);
    if (this.rafId) cancelAnimationFrame(this.rafId);
  },

  methods: {
    scheduleViewBoxUpdate() {
      if (this.pendingViewBoxUpdate) return;
      this.pendingViewBoxUpdate = true;
      this.rafId = requestAnimationFrame(() => {
        this.applyViewBox();
        this.pendingViewBoxUpdate = false;
        this.rafId = null;
      });
    },
    applyViewBox() {
      if (!this.svg) return;
      const vbString = `${this.vbMinX} ${this.vbMinY} ${this.vbWidth} ${this.vbHeight}`;
      this.svg.setAttribute('viewBox', vbString);
      this.resolveScaleAndOffset();
    },
    resolveScaleAndOffset() {
      if (!this.svg || !this.wrapper) return;
      const wrapperBounds = this.wrapper.getBoundingClientRect();
      const mapBounds = this.svg.getBoundingClientRect();
      this.scale = this.vbWidth / mapBounds.width;
      this.offset = {
        x: mapBounds.left - wrapperBounds.left,
        y: mapBounds.top - wrapperBounds.top,
      };
    },

    onSvgLoaded() {
      const vbStr = this.svg.getAttribute('viewBox');
      if (vbStr) {
        this.originalViewBox = vbStr;
        const [x, y, w, h] = vbStr.split(' ').map(Number);
        this.vbMinX = x;
        this.vbMinY = y;
        this.vbWidth = w;
        this.vbHeight = h;
      }
      this.applyViewBox();
      this.resolveScaleAndOffset();
      if (this.zoomedLocation) {
        this.zoomTo(this.zoomedLocation);
      }

      if (this.initialLocation) {
        this.zoomTo(this.initialLocation);
      }
    },

    getPoint(e) {
      if (e.touches && e.touches.length > 0) {
        return { x: e.touches[0].clientX, y: e.touches[0].clientY };
      }
      return { x: e.clientX, y: e.clientY };
    },

    startPan(e) {
      if (this.disablePannZoom) return;
      if (window.innerWidth >= 768) return;

      if (e.touches && e.touches.length === 2) {
        this.handlePinchStart(e);
        return;
      }

      const point = this.getPoint(e);
      const margin = window.innerWidth * 0.1;
      const isNearEdge = point.x < margin || point.x > window.innerWidth - margin;
      if (isNearEdge) return;

      this.isPanning = true;
      this.startPoint = point;
      this.wrapper.style.cursor = 'grabbing';
    },

    panning(e) {
      // if (this.disablePannZoom || window.innerWidth >= 768) return;
      // if (this.disablePannZoom || window.innerWidth <= 768) return;
      return;


      if (e.touches && e.touches.length === 2) {
        e.preventDefault();
        this.handlePinchMove(e);
        return;
      }

      if (!this.isPanning) return;

      e.preventDefault();

      const currentPoint = this.getPoint(e);
      const dxPx = this.startPoint.x - currentPoint.x;
      const dyPx = this.startPoint.y - currentPoint.y;

      this.vbMinX += dxPx * this.scale;
      this.vbMinY += dyPx * this.scale;

      this.startPoint = currentPoint;
      this.scheduleViewBoxUpdate();
    },

    endPan() {
      this.isPanning = false;
      this.lastPinchDist = null;
      if (this.wrapper) this.wrapper.style.cursor = '';
    },

    handleWheel(e) {
      // if (this.disablePannZoom || window.innerWidth >= 768) return;
      // if (this.disablePannZoom || window.innerWidth <= 768) return;
      return;
      e.preventDefault();

      const zoomSensitivity = 0.001;
      const zoomFactor = 1 + e.deltaY * zoomSensitivity;
      this.performZoom(zoomFactor, e.clientX, e.clientY);
    },

    handlePinchStart(e) {
      const p1 = { x: e.touches[0].clientX, y: e.touches[0].clientY };
      const p2 = { x: e.touches[1].clientX, y: e.touches[1].clientY };
      this.lastPinchDist = Math.hypot(p2.x - p1.x, p2.y - p1.y);
    },

    handlePinchMove(e) {
      return;
      const p1 = { x: e.touches[0].clientX, y: e.touches[0].clientY };
      const p2 = { x: e.touches[1].clientX, y: e.touches[1].clientY };
      const currentDist = Math.hypot(p2.x - p1.x, p2.y - p1.y);
      if (this.lastPinchDist) {
        const zoomFactor = this.lastPinchDist / currentDist;
        const centerX = (p1.x + p2.x) / 2;
        const centerY = (p1.y + p2.y) / 2;
        this.performZoom(zoomFactor, centerX, centerY);
      }

      this.lastPinchDist = currentDist;
    },

    performZoom(factor, clientX, clientY) {
      if (!this.svg) return;
      const rect = this.svg.getBoundingClientRect();
      const clickX = clientX - rect.left;
      const clickY = clientY - rect.top;
      const mouseInSVG_X = this.vbMinX + clickX * this.scale;
      const mouseInSVG_Y = this.vbMinY + clickY * this.scale;

      const oldWidth = this.vbWidth;
      const oldHeight = this.vbHeight;
      const newWidth = oldWidth * factor;
      const newHeight = oldHeight * factor;

      if (newWidth > this.vbWidth * 1.5 || newWidth < 100) return;

      this.vbWidth = newWidth;
      this.vbHeight = newHeight;
      this.vbMinX = mouseInSVG_X - clickX * (newWidth / rect.width);
      this.vbMinY = mouseInSVG_Y - clickY * (newHeight / rect.height);

      this.scheduleViewBoxUpdate();
    },

    zoomTo(location) {
      const [oMinX, oMinY, oWidth, oHeight] = this.originalViewBox.split(' ').map(Number);
      let zoomFactor = 2;

      if (window.innerWidth >= 768 && !this.zoomedLocation) {
        zoomFactor = 1;
      } else {
        zoomFactor = 0.95;
      }

      this.vbWidth = oWidth / zoomFactor;
      this.vbHeight = oHeight / zoomFactor;

      let newMinX = location.coords_x - this.vbWidth / 2;
      let newMinY = location.coords_y - this.vbHeight / 2;

      this.vbMinX = Math.max(oMinX, Math.min(newMinX, oMinX + oWidth - this.vbWidth));
      this.vbMinY = Math.max(oMinY, Math.min(newMinY, oMinY + oHeight - this.vbHeight));

      this.scheduleViewBoxUpdate();
    },

    onMapClick(e) {
      if (!this.svg) return;
      const bound = this.svg.getBoundingClientRect();
      let x = Math.round((e.clientX - bound.left) * this.scale);
      let y = Math.round((e.clientY - bound.top) * this.scale);
      x += this.vbMinX;
      y += this.vbMinY;
      this.coords = { x, y };
    },

    humanizeType(typeString) {
      if (!typeString) return '';
      const spaced = typeString.replace(/_/g, ' ');
      return spaced.replace(/\b\w/g, (char) => char.toUpperCase());
    },
  },
};
</script>

<style lang="scss" scoped>
.map-wrapper {
  @apply bg-dark-gray;
  position: relative;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 0.25rem;
  margin-bottom: 1rem;
  overflow: hidden;
  touch-action: pan-y;
  svg {
    height: 100%;
    width: 100%;
    min-width: max-content;
  }

  @media (max-width: 768px) {
    touch-action: pan-y;
    cursor: grab;
    &:active {
      cursor: grabbing;
    }
  }
}

.map-svg-wrapper {
  position: relative;
}

.vector1,
.vector2 {
  position: absolute;
  left: 15%;
}

.vector1 {
  top: 2%;
}

.vector2 {
  bottom: 2%;
}

.map-header {
  position: absolute;
  left: 7%;
  top: 5%;
  padding: 12px 20px 12px 24px;
  align-items: center;
  // border-bottom: 4px solid #8A4943;
  color: #1A1B1C;
  font-family: Merriweather;
  font-size: clamp(18px, 2vw, 32px);
  font-style: normal;
  line-height: 40px;
  font-weight: 700;
  font-size: 32px;
  width: 100%;
  
  gap: 0.25rem;

  @media lg {
    max-width: min(90%, 600px);    
  }

  .title {
    font-size: clamp(28px, 5vw, 64px);
    line-height: 1.1;
  }

  &.-yellow {
    flex-direction: column;
    align-items: flex-start;
    gap: 0.25rem;
    z-index: 9999;
    transform: translateY(-50%);
    font-size: 24px;
      
   
    font-style: normal;
    font-weight: 700;
    line-height: 64px;

    @screen md {
      font-size: 64px;
    }
    &::after {
      display: none;
    }
  }
}

.map-header::after {
  content: "";
  display: block;
  width: 80px;
  border-bottom: 4px solid #8A4943;
  margin-top: 12px;
}

@media screen and (max-width: 768px) {
  .map-header {
    top: 65%;
    padding: 10px 16px 10px 20px;
    left: 5%;
    font-size: clamp(16px, 4vw, 22px);
    .title {
      font-size: clamp(22px, 7vw, 32px);
    }
  }
}

@media screen and (max-width: 480px) {
  .map-header {
    top: 65%;
    left: 4%;
    padding: 18px 18px 18px 18px;
    gap: 0.2rem;

    .title {
      font-size: 36px;
    }
  }
}
</style>
